// Model entities.
import DisplayItem from '../common/model/entity/DisplayItem/DisplayItem.js';
import Meta from '../common/model/entity/DisplayItem/Meta.js';
import Content from '../common/model/entity/DisplayItem/Content.js';
import Cta from '../common/model/entity/DisplayItem/Cta.js';
import Strategy from '../common/model/entity/DisplayItem/Strategy.js';
import Review from '../common/model/entity/DisplayItem/Review.js';
import Ref from '../common/model/entity/DisplayItem/Ref.js';
import Contest from '../common/model/entity/DisplayItem/Contest.js';
import Embed from '../common/model/entity/DisplayItem/Embed.js';
import Actions from '../common/model/entity/DisplayItem/Actions.js';
import Assets from '../common/model/entity/DisplayItem/Assets.js';
import Profile from '../common/model/entity/DisplayItem/Profile.js';
import OriginalContent from '../common/model/entity/DisplayItem/OriginalContent';

import _ from 'lodash';
/**
 * <pre>
 * DisplayItem factory
 *
 * Summary of responsibilities:
 *
 *  - creates and returns DisplayItem entities.
 * </pre>
 *
 * @author Tim Loram
 * @namespace Factories.DisplayItem
 */

/**
 * Creates and returns a DisplayItem entity from JSON object.
 * HEADS UP - any entity addition here must also be accounted for in PinnedDisplayItem. Need to remove dependency on PinnedDisplayItem eventually
 *
 * @param {Object} displayItemJSON The JSON object containing the displayitem information
 * @returns {Common.Model.Entity.DisplayItem}
 *
 * @memberOf Factories.DisplayItem
 */
export function createDisplayItemEntityFromJSON(displayItemJSON) {
  // Create the Meta entity.
  const metaEntity = new Meta(
    displayItemJSON.timestamp,
    displayItemJSON.network,
    displayItemJSON.itemClass,
    displayItemJSON.contentType,
    displayItemJSON.tags,
    displayItemJSON.pinned
  );

  // Create the Content entity.
  const contentEntity = new Content(
    displayItemJSON.heading,
    displayItemJSON.byline,
    displayItemJSON.content,
    displayItemJSON.contentRendered,
    displayItemJSON.caption,
    displayItemJSON.link,
    displayItemJSON.linkTitle,
    displayItemJSON.linkText,
    displayItemJSON.displayedUrls
  );

  // Create the originalContent Entity (quoted tweet)
  const originalContentEntity = !_.isEmpty(displayItemJSON.originalContent) ?
    new OriginalContent({
      assets:        displayItemJSON.originalContent.media,
      author:        displayItemJSON.originalContent.author,
      createDate:    displayItemJSON.originalContent.createDate,
      displayedUrls: displayItemJSON.originalContent.displayedUrls,
      id:            `original-${displayItemJSON.id}`,
      itemClass:     displayItemJSON.itemClass,
      network:       displayItemJSON.network,
      text:          displayItemJSON.originalContent.text,
      url:           displayItemJSON.originalContent.url,
    }) :
    {};

  // Create the Cta entity.
  const ctaEntity = new Cta(
    displayItemJSON.ctaHeadline,
    displayItemJSON.ctaByline,
    displayItemJSON.ctaDescription,
    displayItemJSON.ctaPrice,
    displayItemJSON.ctaButtonText,
    displayItemJSON.ctaLink,
    displayItemJSON.ctaFeatureAssetRef,
    displayItemJSON.ctaThumbnailAssetRef
  );

  // Create the Strategy entity.
  const strategyEntity = new Strategy(displayItemJSON.displayStrategy, displayItemJSON.detailsStrategy);

  // Create the Review entity.
  const reviewEntity = new Review(
    displayItemJSON.rating,
    displayItemJSON.ratingRange,
    displayItemJSON.reviewLikes,
    displayItemJSON.productId,
    displayItemJSON.metainfo,
    displayItemJSON.classification,
    displayItemJSON.context,
    displayItemJSON.extendedProperties
  );

  // Create the Ref entity.
  const refEntity = new Ref(
    displayItemJSON.displayStrategyRef,
    displayItemJSON.contentTypeRef,
    displayItemJSON.externalServiceRef
  );

  // Create the Contest entity.
  const contestfEntity = new Contest(displayItemJSON.score, displayItemJSON.publicWinnerStatus);

  // Create the Embed entity.
  const embedEntity = new Embed(displayItemJSON.isEmbed, displayItemJSON.embedType);

  // Create the Profile entity.
  const profileEntity = new Profile(
    displayItemJSON.profile.userid,
    displayItemJSON.profile.username,
    displayItemJSON.profile.handle,
    displayItemJSON.profile.avatar,
    displayItemJSON.profile.avatarRef
  );

  // Create the Assets entity.
  const assetsEntity = new Assets(
    displayItemJSON.assets.main,
    displayItemJSON.assets.thumbnail,
    displayItemJSON.assets.background,
    displayItemJSON.assets.sizes
  );

  // Create the Actions entity.
  const actionsEntity = new Actions(
    displayItemJSON.actions.network,
    displayItemJSON.actions.profile,
    displayItemJSON.actions.platform,
    displayItemJSON.actions.sharing
  );

  // Create the DisplayItem entity.
  const displayItemEntity = new DisplayItem(
    displayItemJSON.id,
    displayItemJSON.extId,
    metaEntity,
    contentEntity,
    ctaEntity,
    strategyEntity,
    reviewEntity,
    refEntity,
    contestfEntity,
    embedEntity,
    profileEntity,
    assetsEntity,
    actionsEntity,
    originalContentEntity
  );

  return displayItemEntity;
}
